import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useFormik } from "formik";
import { toast } from "sonner";
import { silaeValidationSchema } from "@/schemas/silaeSchema";
import { generateCodePreview } from "@/services/generatedCodeService";
import { useCodeGeneration } from "./useCodeGeneration";
import { useSilaeVariables } from "./useSilaeVariables";
import type {
  SilaeFormData,
  SilaeBranchFormData,
  SilaeBonusFormData,
  SilaeSourceFormData,
  StructureType,
} from "@/types/silae";

// Vérifie qu'une source (et ses enfants) advanced_variable avec variables de sortie a son post-call défini
function sourceMissingPostCall(
  source: SilaeSourceFormData,
  findVar: (
    code: string,
  ) => { parameters?: Array<{ isOutputParam?: boolean }> } | null,
): boolean {
  if (source.sourceType === "advanced_variable" && source.advancedVariable) {
    const variable = findVar(source.advancedVariable);
    if (variable?.parameters?.some((p) => p.isOutputParam)) {
      const varName = (source.advancedVariablePostCallVarName || "").trim();
      const expr = (source.advancedVariablePostCallExpression || "").trim();
      if (!varName || !expr) return true;
    }
  }
  // Parcourir les enfants d'une expression complexe
  if (
    source.sourceType === "complex_expression" &&
    Array.isArray(source.complexExpression)
  ) {
    for (const child of source.complexExpression as SilaeSourceFormData[]) {
      if (sourceMissingPostCall(child, findVar)) return true;
    }
  }
  return false;
}

// Helper pour créer une source vide
export const createEmptySource = (): SilaeSourceFormData => ({
  id: `source_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  hasConditions: false,
  conditions: [],
  conditionsOperators: [],
  elseSource: null,
  sourceType: "fixed",
  amount: 0,
  evpTitle: "",
  evpDefaultValue: "",
  salaryPercentage: "100",
  storedVariableName: "",
  advancedVariable: "",
  advancedVariablePercentage: "100",
  advancedVariableEnableAdditional: false,
  advancedVariableAdditionalOperator: "+",
  advancedVariableAdditionalValue: "0",
  advancedVariableParameters: [],
  advancedVariablePostCallVarName: "",
  advancedVariablePostCallExpression: "",
  complexExpression: [],
  questionnaireVariableType: undefined,
  questionnaireVariableIndex: undefined,
  questionnaireVariableLabel: undefined,
  questionnaireVariablePercentage: "100",
  questionnaireVariableValue: "",
  questionnaireVariableOperator: "=",
  manualFormula: "",
  cumulPeriod: "year_current",
  cumulType: "0",
  cumulCustomStartDate: "",
  cumulCustomEndDate: "",
  cumulDivisor: "",
  cumulStockVarName: "",
  cumulStockVarPeriod: "year_current",
  cumulStockVarDateDebut: "",
  cumulStockVarDateFin: "",
  cumulStockVarPercentage: "100",
  cumulStockVarEnableAdditional: false,
  cumulStockVarAdditionalOperator: "+",
  cumulStockVarAdditionalValue: "0",
  enableEvpForce: false,
  evpForceTitle: "",
});

// Helper pour créer une prime vide
export const createEmptyBonus = (): SilaeBonusFormData => ({
  hasConditions: false,
  conditions: [],
  conditionsOperators: [],
  rubriqueNumber: "",
  calculMethod: undefined,
  bases: [createEmptySource()],
  tauxs: [createEmptySource()],
  libelle: "",
  enableStockage: false,
  stockageVariableName: "",
  stockageVariableType: "bases",
  stockageCustomExpression: "",
  enableMemos: false,
  memoLines: "",
  enableCounterFeeding: false,
  counterType: "",
  counterValueType: "bases",
  counterCustomExpression: "",
  enableDsnPeriodicity: false,
  dsnDateDebut: "Date(0,0,0)",
  dsnDateFin: "Date(0,0,0)",
  bloc52Code: "",
  bloc52CustomCode: "",
});

// Helper pour créer une branche vide
export const createEmptyBranch = (): SilaeBranchFormData => ({
  // hasNestedCondition: false,
  bonuses: [createEmptyBonus()],
});

/**
 * Hook personnalisé pour la gestion du wizard SILAE avec Formik
 *
 * **Architecture simplifiée :**
 * - Redux est la source de vérité unique pour les données du formulaire
 * - Formik affiche les données de Redux et dispatch les changements vers Redux
 * - Formik utilise `enableReinitialize: true` pour se mettre à jour automatiquement quand Redux change
 * - Pas de synchronisation bidirectionnelle complexe, juste un flux unidirectionnel : Formik → Redux → Formik
 */
export const useSilaeWizardFormik = () => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  // Utiliser Redux pour la persistance
  const {
    formData: persistedFormData,
    generatedCode,
    currentStep: persistedCurrentStep,
    setFormData,
    setGeneratedCode,
    setCurrentStep,
    nextStep,
    previousStep,
    resetForm,
  } = useCodeGeneration();

  // useSilaeVariables reste utilisé pour les checks post-call côté UI
  const { findVariable: findVariableFromRedux } = useSilaeVariables();

  const findVariable = useCallback(
    (code: string) => {
      return findVariableFromRedux(code) || null;
    },
    [findVariableFromRedux],
  );

  // Valeurs initiales du formulaire depuis Redux (ou valeurs par défaut)
  // Mémorisé une seule fois au montage pour éviter les réinitialisations
  const initialValues: SilaeFormData = useMemo(() => {
    return (
      persistedFormData || {
        structureType: "simple",
        hasExclusions: false,
        exclusions: undefined,
        exclusionsOperators: [],
        conditions: [],
        conditionsOperators: [],
        ifBranch: createEmptyBranch(),
        comment: "",
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Pas de dépendance pour éviter la réinitialisation

  // ===== Gestion du Code (défini AVANT Formik) =====

  // Génération de code via API backend
  const handleGenerateCode = useCallback(
    async (formData: SilaeFormData) => {
      setIsGenerating(true);

      try {
        const code = await generateCodePreview(formData);
        setGeneratedCode(code);
        toast.success("Code SILAE généré avec succès !");
      } catch (err) {
        console.error("Erreur lors de la génération:", err);
        toast.error("Erreur lors de la génération du code");
      } finally {
        setIsGenerating(false);
      }
    },
    [setGeneratedCode],
  );

  // Configuration Formik avec les valeurs de Redux
  const formik = useFormik<SilaeFormData>({
    initialValues,
    enableReinitialize: false, // Désactivé pour éviter le cycle de réinitialisation
    validationSchema: silaeValidationSchema,
    onSubmit: async (values) => {
      await handleGenerateCode(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Synchroniser les changements de Formik vers Redux (avec debounce)
  // Cette synchronisation se fait uniquement quand les valeurs changent vraiment
  const previousValuesRef = useRef<string>(JSON.stringify(initialValues));
  const isInitializingRef = useRef<boolean>(false);

  useEffect(() => {
    const currentValuesString = JSON.stringify(formik.values);
    const previousValuesString = previousValuesRef.current;

    // Si on est en train d'initialiser elseBranch, ignorer la synchronisation
    if (isInitializingRef.current) {
      isInitializingRef.current = false;
      previousValuesRef.current = currentValuesString;
      return;
    }

    // Ne synchroniser que si les valeurs ont vraiment changé
    if (currentValuesString !== previousValuesString) {
      const timeoutId = setTimeout(() => {
        setFormData(formik.values);
        previousValuesRef.current = currentValuesString;
      }, 500); // Debounce de 500ms

      return () => clearTimeout(timeoutId);
    }

    // Retourner une fonction vide si pas de timeout
    return () => {};
  }, [formik.values, setFormData]);

  // Utiliser currentStep depuis Redux (avec conversion 1-based pour compatibilité)
  const currentStep = persistedCurrentStep + 1;

  // Initialiser elseBranch quand structureType devient "if_else"
  useEffect(() => {
    if (
      formik.values.structureType === "if_else" &&
      !formik.values.elseBranch
    ) {
      // Marquer qu'on est en train d'initialiser pour éviter la boucle infinie
      isInitializingRef.current = true;
      formik.setFieldValue("elseBranch", createEmptyBranch());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.structureType, formik.values.elseBranch]);

  // Calcul du nombre total d'étapes selon la structure
  const totalSteps = useMemo(() => {
    switch (formik.values.structureType) {
      case "simple":
        return 4; // Structure + Config + Validation + generation
      case "with_conditions":
        return 5; // Structure + Conditions + Config if branch + Validation + generation
      case "if_else":
        return 6; // Structure + Conditions + Config IF branch + Config else branch + Validation + generation (if_else)
      default:
        return 4; // Structure + Conditions + Config if branch + Validation + generation
    }
  }, [formik.values.structureType]);

  // ===== Helper Functions =====

  // Mise à jour d'une branche spécifique
  const updateBranch = useCallback(
    (
      branch: "ifBranch" | "elseBranch",
      updates: Partial<SilaeBranchFormData>,
    ) => {
      formik.setFieldValue(branch, {
        ...formik.values[branch],
        ...updates,
      });
    },
    [formik],
  );

  // Mise à jour d'une prime spécifique dans une branche
  const updateBonus = useCallback(
    (
      branch: "ifBranch" | "elseBranch",
      bonusIndex: number,
      updates: Partial<SilaeBonusFormData>,
    ) => {
      const newBonuses = [...(formik.values[branch]?.bonuses || [])];
      newBonuses[bonusIndex] = {
        ...newBonuses[bonusIndex],
        ...updates,
      };
      formik.setFieldValue(`${branch}.bonuses`, newBonuses);
    },
    [formik],
  );

  // Ajouter une prime à une branche
  const addBonus = useCallback(
    (branch: "ifBranch" | "elseBranch") => {
      const newBonuses = [
        ...(formik.values[branch]?.bonuses || []),
        createEmptyBonus(),
      ];
      formik.setFieldValue(`${branch}.bonuses`, newBonuses);
    },
    [formik],
  );

  // Supprimer une prime d'une branche
  const removeBonus = useCallback(
    (branch: "ifBranch" | "elseBranch", bonusIndex: number) => {
      const newBonuses = formik.values[branch]?.bonuses.filter(
        (_, i) => i !== bonusIndex,
      );
      // Garder au moins une prime
      if (newBonuses && newBonuses.length === 0) {
        newBonuses.push(createEmptyBonus());
      }
      formik.setFieldValue(`${branch}.bonuses`, newBonuses);
    },
    [formik],
  );

  // Ajouter une source à bases ou tauxs d'une prime
  const addSource = useCallback(
    (
      branch: "ifBranch" | "elseBranch",
      bonusIndex: number,
      sourceType: "bases" | "tauxs",
    ) => {
      const newBonuses = [...(formik.values[branch]?.bonuses || [])];
      newBonuses[bonusIndex] = {
        ...newBonuses[bonusIndex],
        [sourceType]: [
          ...newBonuses[bonusIndex][sourceType],
          createEmptySource(),
        ],
      };
      formik.setFieldValue(`${branch}.bonuses`, newBonuses);
    },
    [formik],
  );

  // Supprimer une source de bases ou tauxs d'une prime
  const removeSource = useCallback(
    (
      branch: "ifBranch" | "elseBranch",
      bonusIndex: number,
      sourceType: "bases" | "tauxs",
      sourceIndex: number,
    ) => {
      const newBonuses = [...(formik.values[branch]?.bonuses || [])];
      const newSources = newBonuses[bonusIndex][sourceType].filter(
        (_, i) => i !== sourceIndex,
      );
      // Garder au moins une source
      if (newSources.length === 0) {
        newSources.push(createEmptySource());
      }
      newBonuses[bonusIndex] = {
        ...newBonuses[bonusIndex],
        [sourceType]: newSources,
      };
      formik.setFieldValue(`${branch}.bonuses`, newBonuses);
    },
    [formik],
  );

  // Mettre à jour une source spécifique
  const updateSource = useCallback(
    (
      branch: "ifBranch" | "elseBranch",
      bonusIndex: number,
      sourceType: "bases" | "tauxs",
      sourceIndex: number,
      updates: Partial<SilaeSourceFormData>,
    ) => {
      const newBonuses = [...(formik.values[branch]?.bonuses || [])];
      const newSources = [...newBonuses[bonusIndex][sourceType]];
      newSources[sourceIndex] = {
        ...newSources[sourceIndex],
        ...updates,
      };
      newBonuses[bonusIndex] = {
        ...newBonuses[bonusIndex],
        [sourceType]: newSources,
      };
      formik.setFieldValue(`${branch}.bonuses`, newBonuses);
    },
    [formik],
  );

  // ===== Validation =====

  // Validation par étape
  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    await formik.validateForm();

    const validateBonus = (bonus: SilaeBonusFormData | undefined): boolean => {
      if (!bonus) return false;
      if (!bonus.rubriqueNumber || !bonus.calculMethod || !bonus.libelle)
        return false;
      if (!bonus.bases || bonus.bases.length === 0) return false;
      if (
        (bonus.calculMethod === "1" || bonus.calculMethod === "3") &&
        (!bonus.tauxs || bonus.tauxs.length === 0)
      )
        return false;
      if (bonus.enableDsnPeriodicity) {
        if (
          !bonus.dsnDateDebut ||
          bonus.dsnDateDebut === "Date(0,0,0)" ||
          bonus.dsnDateDebut === "Date(0, 0, 0)"
        )
          return false;
        if (
          !bonus.dsnDateFin ||
          bonus.dsnDateFin === "Date(0,0,0)" ||
          bonus.dsnDateFin === "Date(0, 0, 0)"
        )
          return false;
        if (!bonus.bloc52Code) return false;
        if (bonus.bloc52Code === "custom" && !bonus.bloc52CustomCode)
          return false;
      }
      return true;
    };

    // Vérification simple basée sur l'étape
    switch (currentStep) {
      case 1:
        // Étape 1 : Structure et Exclusions
        return (
          !!formik.values.structureType ||
          (formik.values.hasExclusions && !!formik.values.hasExclusions)
        );

      case 2: {
        // Étape 2 : Configuration (simple) ou Conditions (with_conditions/if_else)
        if (formik.values.structureType === "simple") {
          const firstBonus = formik.values.ifBranch.bonuses[0];
          return validateBonus(firstBonus);
        }
        // Pour with_conditions et if_else, validation des conditions (peut être vide)
        return true;
      }

      case 3: {
        // Étape 3 : Configuration (with_conditions/if_else) ou Validation (simple)
        if (formik.values.structureType !== "simple") {
          const firstBonus = formik.values.ifBranch.bonuses[0];
          return validateBonus(firstBonus);
        }
        return true;
      }

      case 4: {
        // Étape 4 : Config ELSE (if_else) ou Validation (with_conditions)
        if (formik.values.structureType === "if_else") {
          const firstBonus = formik.values.elseBranch?.bonuses?.[0];
          return validateBonus(firstBonus);
        }
        return true;
      }

      case 5:
        // Étape 5 : Validation finale (if_else)
        return true;

      default:
        return true;
    }
  }, [currentStep, formik]);

  // Helper pour vérifier si une branche a des erreurs (basé uniquement sur les valeurs)
  const hasBranchErrors = useCallback(
    (branch: "ifBranch" | "elseBranch"): boolean => {
      // Si Yup a détecté des erreurs dans la branche (inclut les expressions complexes imbriquées)
      if (formik.errors[branch]) return true;

      const branchValues = formik.values[branch];

      if (
        !branchValues ||
        !branchValues.bonuses ||
        branchValues.bonuses.length === 0
      ) {
        return true;
      }

      const isDateZero = (d: string) =>
        !d || d === "Date(0,0,0)" || d === "Date(0, 0, 0)";

      return branchValues.bonuses.some((bonus) => {
        if (!bonus.rubriqueNumber || !bonus.calculMethod || !bonus.libelle)
          return true;
        if (!bonus.bases || bonus.bases.length === 0) return true;
        if (
          (bonus.calculMethod === "1" || bonus.calculMethod === "3") &&
          (!bonus.tauxs || bonus.tauxs.length === 0)
        )
          return true;
        if (bonus.enableDsnPeriodicity) {
          if (isDateZero(bonus.dsnDateDebut)) return true;
          if (isDateZero(bonus.dsnDateFin)) return true;
          if (!bonus.bloc52Code) return true;
          if (bonus.bloc52Code === "custom" && !bonus.bloc52CustomCode)
            return true;
        }
        // Vérifier que toutes les sources ont leur post-call défini si nécessaire
        for (const source of [...(bonus.bases || []), ...(bonus.tauxs || [])]) {
          if (sourceMissingPostCall(source, findVariable)) return true;
        }
        return false;
      });
    },
    [formik.values, formik.errors, findVariable],
  );

  // Vérification en temps réel des erreurs de l'étape actuelle
  const hasCurrentStepErrors = useCallback((): boolean => {
    switch (currentStep) {
      case 1:
        return (
          !formik.values.structureType ||
          !!formik.errors.structureType ||
          !!formik.errors.exclusions
        );

      case 2: {
        if (formik.values.structureType === "simple") {
          // Vérifier les erreurs de la branche IF
          return hasBranchErrors("ifBranch");
        }
        // Pour with_conditions et if_else, vérifier les erreurs de conditions
        return !!formik.errors.conditions;
      }

      case 3: {
        if (formik.values.structureType !== "simple") {
          // Vérifier les erreurs de la branche IF
          return hasBranchErrors("ifBranch");
        }
        return false;
      }

      case 4: {
        if (formik.values.structureType === "if_else") {
          // Vérifier les erreurs de la branche ELSE
          return hasBranchErrors("elseBranch");
        }
        return false;
      }

      case 5:
        return false;

      default:
        return false;
    }
  }, [currentStep, formik.values, formik.errors, hasBranchErrors]);

  // ===== Chargement depuis Redux =====

  // Charge des données externes dans Formik (appelé après dispatch depuis le wizard)
  const loadFormData = useCallback(
    (data: SilaeFormData) => {
      formik.resetForm({ values: data });
      previousValuesRef.current = JSON.stringify(data);
    },
    // formik.resetForm est stable, pas besoin de l'inclure
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // ===== Réinitialisation =====

  // Réinitialisation du wizard
  const resetWizard = useCallback(() => {
    // Créer des valeurs vides pour le reset
    const emptyFormData: SilaeFormData = {
      structureType: "simple",
      hasExclusions: false,
      exclusions: undefined,
      exclusionsOperators: [],
      conditions: [],
      conditionsOperators: [],
      ifBranch: createEmptyBranch(),
      elseBranch: undefined,
    };

    // Réinitialiser Redux (cela réinitialise aussi currentStep à 0)
    resetForm();

    // Réinitialiser Formik avec les valeurs vides
    formik.setValues(emptyFormData);
    formik.setErrors({});
    formik.setTouched({});

    // Réinitialiser la référence pour éviter la resynchronisation des anciennes valeurs
    previousValuesRef.current = JSON.stringify(emptyFormData);

    // S'assurer que l'étape est bien réinitialisée (redundant mais sécurisé)
    setCurrentStep(0);
  }, [formik, resetForm, setCurrentStep]);

  // ===== Navigation =====

  // Navigation vers l'étape suivante
  const goToNextStep = useCallback(async () => {
    const isValid = await validateCurrentStep();

    if (!isValid) {
      toast.error("Veuillez corriger les erreurs avant de continuer");
      return false;
    }

    if (currentStep < totalSteps) {
      nextStep(); // Utiliser l'action Redux (0-based)
      return true;
    }

    // Si on est à la dernière étape, réinitialiser le formulaire
    if (currentStep === totalSteps) {
      resetWizard();
      toast.success("Formulaire réinitialisé avec succès !");
      return true;
    }

    return false;
  }, [currentStep, totalSteps, validateCurrentStep, nextStep, resetWizard]);

  // Navigation vers l'étape précédente
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      previousStep(); // Utiliser l'action Redux (0-based)
      return true;
    }
    return false;
  }, [currentStep, previousStep]);

  // Aller directement à une étape
  const goToStep = useCallback(
    (step: number) => {
      if (step >= 1 && step <= totalSteps && step <= currentStep + 1) {
        setCurrentStep(step - 1); // Convertir en 0-based pour Redux
        return true;
      }
      return false;
    },
    [currentStep, totalSteps, setCurrentStep],
  );

  // Mise à jour de la structure du wizard
  const updateStructureType = useCallback(
    (structure: StructureType) => {
      formik.setFieldValue("structureType", structure);
      setGeneratedCode(""); // Utiliser Redux
    },
    [formik, setGeneratedCode],
  );

  // ===== Actions sur le Code =====

  // Copie du code généré
  const handleCopyCode = async () => {
    if (!generatedCode) return;

    try {
      await navigator.clipboard.writeText(generatedCode);
      toast.success("Code copié dans le presse-papiers !");
    } catch {
      toast.error("Erreur lors de la copie");
    }
  };

  // Téléchargement du code
  const handleDownloadCode = () => {
    if (!generatedCode) return;

    const firstBonus = formik.values.ifBranch.bonuses[0];
    const filename = firstBonus?.libelle || "code";

    const blob = new Blob([generatedCode], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `silae_${filename}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Code téléchargé !");
  };

  // Édition du code
  const handleEditCode = (editedCode: string) => {
    setGeneratedCode(editedCode); // Sauvegarder dans Redux
  };

  return {
    // État du wizard
    currentStep,
    totalSteps,
    structureType: formik.values.structureType,

    // État du formulaire
    formik,
    generatedCode,
    isGenerating,

    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,
    updateStructureType,
    validateCurrentStep,
    hasCurrentStepErrors,

    // Actions sur les branches
    updateBranch,

    // Actions sur les primes
    updateBonus,
    addBonus,
    removeBonus,

    // Actions sur les sources
    addSource,
    removeSource,
    updateSource,

    // Actions sur le code
    handleCopyCode,
    handleDownloadCode,
    handleEditCode,
    resetWizard,
    loadFormData,
    handleGenerateCode,

    // Helpers
    createEmptySource,
    createEmptyBonus,
    createEmptyBranch,
  };
};
